import Vue from 'vue'
import VueRouter from 'vue-router'
import store from '@/store'

Vue.use(VueRouter)

const routes = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/Login.vue'),
    meta: { guest: true }
  },
  {
    path: '/',
    component: () => import('@/layouts/MainLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        redirect: 'dashboard'
      },
      {
        path: 'dashboard',
        name: 'Dashboard',
        component: () => import('@/views/Dashboard.vue'),
        meta: { permission: 'dashboard' }
      },
      {
        path: 'upload',
        name: 'Upload',
        component: () => import('@/views/Upload.vue'),
        meta: { permission: 'photo_upload' }
      },
      {
        path: 'my-uploads',
        name: 'MyUploads',
        component: () => import('@/views/MyUploads.vue'),
        meta: { permission: 'my_uploads' }
      },
      {
        path: 'search',
        name: 'Search',
        component: () => import('@/views/SearchPhoto.vue'),
        meta: { permission: 'photo_search' }
      },
      {
        path: 'download-history',
        name: 'DownloadHistory',
        component: () => import('@/views/DownloadHistory.vue'),
        meta: { permission: 'download_history' }
      },
      {
        path: 'upload-stats',
        name: 'UploadStats',
        component: () => import('@/views/UploadStats.vue'),
        meta: { permission: 'upload_stats' }
      },
      {
        path: 'good-photos-report',
        name: 'GoodPhotosReport',
        component: () => import('@/views/GoodPhotosReport.vue'),
        meta: { permission: 'dashboard' }
      },
      {
        path: 'users',
        name: 'Users',
        component: () => import('@/views/Users.vue'),
        meta: { permission: 'user_management' }
      },
      {
        path: 'roles',
        name: 'Roles',
        component: () => import('@/views/Roles.vue'),
        meta: { permission: 'role_management' }
      },
      {
        path: 'menus',
        name: 'MenuManagement',
        component: () => import('@/views/MenuManagement.vue'),
        meta: { permission: 'menu_management' }
      },
      {
        path: 'locations',
        name: 'Locations',
        component: () => import('@/views/Locations.vue'),
        meta: { permission: 'location_management' }
      },
      {
        path: 'settings',
        name: 'Settings',
        component: () => import('@/views/Settings.vue'),
        meta: { permission: 'system_settings' }
      },
      {
        path: 'prompt-template',
        name: 'PromptTemplate',
        component: () => import('@/views/PromptTemplate.vue'),
        meta: { permission: 'prompt_template' }
      },
      {
        path: 'watermark-management',
        name: 'WatermarkManagement',
        component: () => import('@/views/WatermarkManagement.vue'),
        meta: { permission: 'watermark_management' }
      },
      {
        path: 'ai-logs',
        name: 'AiGenerationLogs',
        component: () => import('@/views/AiGenerationLogs.vue'),
        meta: { permission: 'ai_generation_logs' }
      },
      {
        path: 'folder-management',
        name: 'FolderManagement',
        component: () => import('@/views/FolderManagement.vue'),
        meta: { permission: 'folder_management' }
      },
      {
        path: 'action-logs',
        name: 'ActionLogs',
        component: () => import('@/views/ActionLogs.vue'),
        meta: { permission: 'action_logs' }
      },
      {
        path: 'social-integration',
        name: 'SocialIntegration',
        component: () => import('@/views/SocialIntegration.vue'),
        meta: { permission: 'social_integration' }
      },
      {
        path: 'profile-settings',
        name: 'ProfileSettings',
        component: () => import('@/views/ProfileSettings.vue'),
      },
    ]
  },
  {
    path: '*',
    redirect: '/'
  }
]

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes
})

// Suppress NavigationDuplicated errors
const originalPush = VueRouter.prototype.push
VueRouter.prototype.push = function push(location) {
  return originalPush.call(this, location).catch(err => {
    if (err.name !== 'NavigationDuplicated') {
      throw err
    }
  })
}

const originalReplace = VueRouter.prototype.replace
VueRouter.prototype.replace = function replace(location) {
  return originalReplace.call(this, location).catch(err => {
    if (err.name !== 'NavigationDuplicated') {
      throw err
    }
  })
}

// Navigation guard
router.beforeEach((to, from, next) => {
  const isAuthenticated = store.getters['auth/isAuthenticated']
  const userPermissions = store.getters['auth/permissions'] || []
  
  // Check if route or its parent requires auth
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth)
  const isGuestRoute = to.matched.some(record => record.meta.guest)
  
  // Guest only routes (login page)
  if (isGuestRoute && isAuthenticated) {
    // If already going to Dashboard, don't redirect again
    if (to.name === 'Dashboard') {
      return next()
    }
    return next({ name: 'Dashboard' })
  }
  
  // Protected routes - redirect to login if not authenticated
  if (requiresAuth && !isAuthenticated) {
    // If already going to Login, don't redirect again
    if (to.name === 'Login') {
      return next()
    }
    return next({ name: 'Login' })
  }
  
  // Permission check - but Dashboard should always be accessible if authenticated
  if (to.meta.permission && isAuthenticated && to.name !== 'Dashboard') {
    if (!userPermissions.includes(to.meta.permission)) {
      // If already going to Dashboard, don't redirect again
      if (to.name === 'Dashboard') {
        return next()
      }
      return next({ name: 'Dashboard' })
    }
  }
  
  next()
})

export default router
